import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import partnerApi, { errMsg } from '../api';

const STATUS_FILTER = ['all', 'active', 'inactive'];

function StatusBadge({ product }) {
  const overrideStatus = product._override?.status;
  const crawlerActive = product.is_active !== false;

  if (overrideStatus === 'inactive') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
        Deactivated
      </span>
    );
  }
  if (!crawlerActive) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-700">
        Unlisted
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
      Active
    </span>
  );
}

function formatPrice(price) {
  if (price == null) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [nextKey, setNextKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async (startKey = null, replace = true) => {
    replace ? setLoading(true) : setLoadingMore(true);
    try {
      const params = { page_size: 20 };
      if (startKey) params.start_key = startKey;
      const { data } = await partnerApi.get('/products', { params });
      setProducts((prev) => replace ? data.products : [...prev, ...data.products]);
      setNextKey(data.next_key || null);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load products'));
    } finally {
      replace ? setLoading(false) : setLoadingMore(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Client-side filter
  const visible = products.filter((p) => {
    const overrideStatus = p._override?.status;
    const isActive = overrideStatus !== 'inactive' && p.is_active !== false;
    if (filter === 'active' && !isActive) return false;
    if (filter === 'inactive' && isActive) return false;
    if (search && !(p.title || p.brand || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-ink">Products</h1>
          <p className="mt-0.5 text-sm text-ink-secondary">
            {loading ? 'Loading…' : `${products.length} product${products.length === 1 ? '' : 's'} indexed`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          placeholder="Search by title or brand…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-hairline px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
        />
        <div className="flex items-center gap-1 rounded-lg border border-hairline bg-surface p-1">
          {STATUS_FILTER.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded px-3 py-1 text-xs font-medium capitalize transition-colors ${
                filter === f
                  ? 'bg-raspberry-600 text-white'
                  : 'text-ink-secondary hover:text-ink'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-16 text-center text-sm text-ink-muted">Loading your products…</div>
      ) : visible.length === 0 ? (
        <EmptyState hasProducts={products.length > 0} />
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-hairline">
            <table className="w-full text-sm">
              <thead className="border-b border-hairline bg-surface-page">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-muted">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-muted">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-muted">Locks</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline bg-surface">
                {visible.map((p) => (
                  <tr key={p.mira_sku} className="hover:bg-surface-page">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.image_urls?.[0] && (
                          <img
                            src={p.image_urls[0]}
                            alt=""
                            className="h-10 w-10 rounded-lg object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div>
                          <div className="font-medium text-ink line-clamp-1">{p.title || p.brand || 'Untitled'}</div>
                          <div className="text-xs text-ink-muted">{p.mira_sku}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-ink">{formatPrice(p.price)}</td>
                    <td className="px-4 py-3"><StatusBadge product={p} /></td>
                    <td className="px-4 py-3 text-xs text-ink-muted">
                      {(p._override?.locked_fields || []).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/products/${p.mira_sku}`}
                        className="text-xs font-medium text-raspberry-600 hover:underline"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {nextKey && (
            <div className="mt-4 text-center">
              <button
                onClick={() => load(nextKey, false)}
                disabled={loadingMore}
                className="rounded-lg border border-hairline px-4 py-2 text-sm text-ink-secondary hover:bg-surface-page disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EmptyState({ hasProducts }) {
  if (hasProducts) {
    return (
      <div className="rounded-xl border border-hairline bg-surface py-16 text-center">
        <p className="text-sm font-medium text-ink">No products match your filter</p>
        <p className="mt-1 text-xs text-ink-muted">Try clearing the search or changing the status filter.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-hairline bg-surface px-6 py-16 text-center">
      <p className="text-sm font-medium text-ink">No products yet</p>
      <p className="mt-1 max-w-sm mx-auto text-xs text-ink-muted">
        Our crawler is still indexing your catalogue. Products usually appear within
        a few hours of your account being approved.
      </p>
    </div>
  );
}
